<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: Template
  Reason: "Berufe (TAA)" tab of the unified xx Side-Car Playground. Picks an
          xx.XxPerson of the PLAYGROUND_TypeA type and edits its internal typed
          side-car (xx.XxPersonData) through the framework's generic CRUD form.
          The occupation field renders as the framework's search-backed value
          list control (uiInputControl = valueListSearch) — this template does
          NOT render any field itself. Both host foreign keys are pinned via
          importObjectContextDto, because they are the unique key of the
          instance and must not be re-pointed from inside the form.
-->
<div class="flex flex-column gap-3 p-3">

    <!-- Tab header — rendered immediately, so the tab is never blank on first open -->
    <div class="flex align-items-start justify-content-between gap-3">
        <div class="flex flex-column gap-1">
            <h2 class="m-0">Berufe (TAA)</h2>
            <span class="text-color-secondary">
                Erfassung des internen typisierten Side-Cars einer Person. Das Feld „Beruf“ wird vom
                Framework als suchbare Werteliste dargestellt: Die Stuttgarter TAA-Berufeliste bleibt
                auf dem Server, das Formular sucht ab zwei Zeichen serverseitig und speichert den
                Berufsschlüssel als cc.FieldValue.
            </span>
        </div>
        <p-button
                label="Neu laden"
                icon="fa-light fa-arrows-rotate"
                severity="secondary"
                [outlined]="true"
                [disabled]="loading"
                (onClick)="reload()">
        </p-button>
    </div>

    <!-- Error state -->
    @if (errorMessage) {
        <p-message severity="error" [text]="errorMessage" styleClass="w-full"></p-message>
    }

    <!-- Success state -->
    @if (successMessage) {
        <p-message severity="success" [text]="successMessage" styleClass="w-full"></p-message>
    }

    <!-- Person type not seeded on this environment -->
    @if (initialized && !loading && !personType) {
        <p-message
                severity="warn"
                [text]="'Personentyp „' + personTypeName + '“ ist auf dieser Umgebung nicht vorhanden — bitte zuerst anlegen.'"
                styleClass="w-full">
        </p-message>
    }

    <!-- Person selector + the field definitions of the type -->
    @if (initialized && personType) {
        <p-panel header="Person auswählen">
            <div class="flex flex-column gap-3">

                <div class="flex align-items-center flex-wrap gap-2">
                    <label for="xx-berufe-person" class="font-medium">Person</label>
                    <p-select
                            inputId="xx-berufe-person"
                            [options]="persons"
                            [(ngModel)]="selectedPerson"
                            (onChange)="onPersonSelect()"
                            [disabled]="loading || persons.length === 0"
                            placeholder="Person auswählen"
                            appendTo="body"
                            styleClass="w-20rem">
                        <ng-template let-person pTemplate="selectedItem">
                            @if (person) {
                                <span>{{ resolvePersonLabel(person) }}</span>
                            }
                        </ng-template>
                        <ng-template let-person pTemplate="item">
                            <span>{{ resolvePersonLabel(person) }}</span>
                        </ng-template>
                    </p-select>
                    <span class="text-color-secondary text-sm">
                        Personentyp: <strong>{{ personTypeName }}</strong>
                    </span>
                </div>

                <!-- Empty state: says what to do next, not just that there is nothing -->
                @if (persons.length === 0 && !loading) {
                    <p-message
                            severity="info"
                            [text]="'Keine Personen des Typs „' + personTypeName + '“ vorhanden. Bitte zuerst im Tab „uf-Erfassung“ eine Person dieses Typs anlegen und anschließend hier auf „Neu laden“ klicken.'"
                            styleClass="w-full">
                    </p-message>
                }

                <!--
                  Diagnostic only: the field definitions of the type. The form below is built from
                  the backend form metadata, never from this list.
                -->
                @if (typeFields.length > 0) {
                    <div class="flex flex-column gap-1">
                        <span class="text-color-secondary text-sm">
                            Felder dieses Typs (aus der Konfiguration):
                        </span>
                        <div class="flex flex-wrap gap-2">
                            @for (typeField of typeFields; track typeField.id) {
                                <p-tag
                                        [value]="typeField.name"
                                        [severity]="isOccupationField(typeField) ? 'success' : 'secondary'"
                                        styleClass="text-xs">
                                </p-tag>
                            }
                        </div>
                    </div>
                }

                @if (typeFields.length > 0 && !hasOccupationField) {
                    <p-message
                            severity="warn"
                            text="Auf diesem Typ ist noch kein Beruf-Feld konfiguriert — das Formular zeigt dann keine Berufssuche."
                            styleClass="w-full">
                    </p-message>
                }
            </div>
        </p-panel>
    }

    <!--
      Loading state. Deliberately OUTSIDE the personType guard: on the very first open the type is
      not resolved yet, and without this the tab would render nothing at all until it is.
    -->
    @if (!initialized || loading) {
        <div class="flex flex-column gap-2">
            <p-skeleton width="14rem" height="1.5rem"></p-skeleton>
            <p-skeleton width="100%" height="2.5rem"></p-skeleton>
            <p-skeleton width="100%" height="2.5rem"></p-skeleton>
            <p-skeleton width="60%" height="2.5rem"></p-skeleton>
            <span class="text-color-secondary text-sm">Daten werden geladen…</span>
        </div>
    }

    <!--
      The real, metadata-driven form. Nothing here renders a field: the backend
      XxPersonDataForm (GenericTemplateTypeInstanceFormObject) emits one field per
      XxPersonTypeField, and the occupation field arrives with
      uiInputControl = "valueListSearch", which CcModule maps onto
      MvsFormFieldValueListSearchComponent (PrimeNG p-autoComplete, serverseitige Suche).

      [importObjectContextDto] pins BOTH host foreign keys (xx.XxPerson + xx.XxPersonType) in
      CREATE as well as in EDIT: they form the unique key xx_person_data_u1, so leaving them
      editable would let the user re-point the instance from inside the form and hit a raw database
      constraint error.

      The single-element @for forces a FRESH form instance whenever the selected person or the
      create/edit mode changes — otherwise the previous person's values would survive.
    -->
    @if (initialized && personType && formVisible) {
        <p-panel [header]="formHeader">
            @for (instanceKey of [formInstanceKey]; track instanceKey) {
                @if (personDataId != null) {
                    <!-- AI: CRUD Object Component — EDIT of the internal typed side-car instance -->
                    <mvs-crud-object-generic
                            [objectType]="personDataObjectType"
                            [objectId]="personDataId"
                            [importObjectContextDto]="hostContextDto"
                            (onChangedObject)="onChangedObject($event)">
                    </mvs-crud-object-generic>
                } @else {
                    <!-- AI: CRUD Object Component — CREATE of the internal typed side-car instance -->
                    <mvs-crud-object-generic
                            [objectType]="personDataObjectType"
                            [defaultCreateDto]="createDto"
                            [importObjectContextDto]="hostContextDto"
                            (onChangedObject)="onChangedObject($event)">
                    </mvs-crud-object-generic>
                }
            }
        </p-panel>
    }

    <!-- No selection yet -->
    @if (initialized && personType && !loading && !selectedPerson && persons.length > 0) {
        <p-message
                severity="info"
                text="Bitte oben eine Person auswählen, um deren interne typisierte Daten zu bearbeiten."
                styleClass="w-full">
        </p-message>
    }
</div>
